'use client'

import { useState } from "react";
import Link from "next/link";
import { useGetEsimByKeyWord, usePublicPlanBySlug } from '@/hooks/use-esim'
import { useEsimSelection } from '@/lib/esim-selection'
import { DataPlan, PublicAllPlansResponse, PublicPlan } from "@/types/esim"
import Flag from '@/components/ui/flag'
import { FilterSelect } from '@/components/ui/filter-select'
import { PlanCard, PlanCardSkeleton } from "@/components/esim/public/plan-card"
import {
  ChevronLeft, ChevronRight, Wifi,
  ArrowRight, Sparkles, Zap, Globe, Signal, Info, Search, X, Shield, Smartphone,
} from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
// ─── Bottom CTA ───────────────────────────────────────────────────────────────

function DestinationCTA() {
  return (
    <div className="mt-8 global-border-radius border border-brand-stroke/40 bg-brand-card p-8 text-center">
      <h2 className="text-xl font-bold text-brand-text 3xl:text-2xl">Ready to travel connected?</h2>
      <p className="mt-2 text-sm text-brand-muted 3xl:text-base">
        Browse plans for other destinations or follow our quick setup guide.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-3">
        <Link
          href="/travel-esim"
          className="inline-flex items-center gap-2 global-border-radius bg-brand-grey px-5 py-2.5 text-sm font-bold text-brand-bg hover:opacity-90 transition-all"
        >
          Browse all destinations
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          href="/travel-esim/install-esim"
          className="inline-flex items-center gap-2 global-border-radius border border-brand-stroke px-5 py-2.5 text-sm font-semibold text-brand-text hover:bg-brand-surface transition-all"
        >
          Setup guide
        </Link>
      </div>
    </div>
  );
}

// ─── Main content ─────────────────────────────────────────────────────────────

export default function DestinationContent({
  initialData,
  countryRegion,
}: {
  initialData: PublicAllPlansResponse | null
  countryRegion: string
}) {
  const selectedSlug = useEsimSelection()
  const { data: selectedPlan } = usePublicPlanBySlug(selectedSlug)

  const [page, setPage] = useState(1)
  const [dataFilter, setDataFilter] = useState("all")
  const [validityFilter, setValidityFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")

  const [hasInteracted, setHasInteracted] = useState(false)
  const isQueryEnabled = hasInteracted || !initialData

  const { data: queryData, isLoading } = useGetEsimByKeyWord(
    isQueryEnabled ? countryRegion : "",
    page,
    { dataGb: dataFilter, validity: validityFilter, planType: typeFilter, sort: "asc" },
  )

  const items: DataPlan[] = isQueryEnabled
    ? (queryData?.items ?? [])
    : (initialData?.items ?? [])
  const meta = isQueryEnabled ? queryData?.meta : initialData?.meta
  const totalPages = meta?.last_page ?? 1
  const totalCount = meta?.total ?? 0
  const currentlyLoading = isQueryEnabled && isLoading

  const destinationName =
    items[0]?.country ||
    items[0]?.region ||
    countryRegion.replace(/(^|\s)(\w)/g, (_: string, sep: string, c: string) => sep + c.toUpperCase())
  const iso2 = items[0]?.iso2 ?? null

  const interact = () => setHasInteracted(true)

  if (!currentlyLoading && isQueryEnabled && items.length === 0) {
    return <NotFound />
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-bg border-b border-brand-stroke/20 py-14 sm:py-20 xl:py-24">
        {/* Ambient green glow — top centre */}
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[560px] w-[560px] rounded-full opacity-[0.10]"
          style={{ background: "radial-gradient(circle, #23ebad 0%, transparent 65%)" }}
        />
        {/* Dot grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,1) 1px, transparent 0)',
            backgroundSize: '36px 36px',
          }}
        />

        <div className="relative z-10 mx-auto max-w-5xl px-4 lg:px-0 flex flex-col items-center text-center gap-6">

          {/* Breadcrumb */}
          <Link
            href="/travel-esim"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted hover:text-brand-text transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            All destinations
          </Link>

          {/* Flag */}
          <div className="flex h-28 w-28 items-center justify-center overflow-hidden global-border-radius shadow-[0_8px_32px_rgba(0,0,0,0.35)]">
            {currentlyLoading ? (
              <div className="h-full w-full bg-brand-surface/70 animate-pulse" />
            ) : iso2 ? (
              <Flag code={iso2.toLowerCase()} width={110} height={110} circle={true} />
            ) : (
              <Globe className="h-10 w-10 text-brand-muted" />
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl lg:text-5xl 3xl:text-7xl font-bold tracking-tight leading-tight">
            {currentlyLoading ? (
              <span className="inline-block h-10 w-72 global-border-radius bg-brand-surface/70 animate-pulse" />
            ) : destinationName ? (
              <>
                eSIM Plans for{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(135deg, #23EBAD 0%, #008080 100%)" }}
                >
                  {destinationName}
                </span>
              </>
            ) : (
              "eSIM Plans"
            )}
          </h1>

          {/* Plan count */}
          {!currentlyLoading && totalCount > 0 && (
            <p className="text-base text-brand-muted 3xl:text-xl">
              {totalCount} plans available · Instant eSIM delivery
            </p>
          )}

          {/* Feature pills — inline in hero */}
          <div className="flex flex-wrap justify-center gap-2">
            {([
              { icon: Zap, label: "Instant delivery" },
              { icon: Signal, label: "4G & 5G speeds" },
              { icon: Smartphone, label: "QR activation" },
              { icon: Shield, label: "No contracts" },
            ] as const).map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="flex items-center gap-1.5 rounded-full border border-brand-stroke/40 bg-brand-surface/60 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-brand-muted"
              >
                <Icon className="h-3.5 w-3.5 text-brand-green" />
                {label}
              </span>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            <Link
              href="/travel-esim"
              className="inline-flex items-center gap-2 rounded-full bg-brand-grey px-5 py-2.5 text-sm font-bold text-brand-bg hover:opacity-90 transition-all"
            >
              Browse all plans
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href="/travel-esim-comparison"
              className="inline-flex items-center gap-2 rounded-full border border-brand-stroke px-5 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-green/30 hover:bg-brand-surface/30 transition-all"
            >
              Compare plans
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 lg:px-0 py-6 flex flex-col gap-6">
        {selectedPlan && (
          <div className="flex items-center justify-between gap-4 global-border-radius border-2 border-brand-green bg-brand-green/5 px-5 py-4">
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="text-base 3xl:text-2xl font-semibold text-brand-text truncate">{selectedPlan.name}</p>
              <p className="text-xs text-brand-muted 3xl:text-sm">
                {selectedPlan.data_plan_type === 'unlimitedStandard' ? 'Unlimited' : `${parseFloat(selectedPlan.data_quota_gb)} GB`}
                {' · '}{selectedPlan.validity_days} days
                {' · '}${selectedPlan.retail_price_converted_after_offer.toFixed(2)} AUD
              </p>
            </div>
            <Link
              href={`/public-esim/checkout?slug=${selectedPlan.slug}`}
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 global-border-radius text-xs font-bold bg-brand-grey text-brand-card hover:bg-brand-grey/80 transition-colors"
            >
              <Wifi className="h-3.5 w-3.5" />
              Get Plan
            </Link>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {[
            {
              value: dataFilter,
              onChange: (v: string) => { interact(); setDataFilter(v); setPage(1) },
              options: [
                { v: "all", l: "All Data" },
                ...[1, 3, 5, 10, 20, 30, 45, 90].map((n) => ({ v: String(n), l: `${n} GB` })),
              ],
            },
            {
              value: validityFilter,
              onChange: (v: string) => { interact(); setValidityFilter(v); setPage(1) },
              options: [
                { v: "all", l: "All Validity" },
                ...[5, 10, 15, 30].map((n) => ({ v: String(n), l: `${n} days` })),
              ],
            },
            {
              value: typeFilter,
              onChange: (v: string) => { interact(); setTypeFilter(v); setPage(1) },
              options: [
                { v: "all", l: "All Types" },
                { v: "fixedData", l: "Fixed Data" },
                { v: "unlimitedStandard", l: "Unlimited" },
              ],
            },
          ].map((f, i) => (
            <FilterSelect key={i} value={f.value} onChange={f.onChange} options={f.options} />
          ))}
          {(dataFilter !== "all" || validityFilter !== "all" || typeFilter !== "all") && (
            <button
              onClick={() => { interact(); setDataFilter("all"); setValidityFilter("all"); setTypeFilter("all"); setPage(1) }}
              className="text-xs text-brand-green underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              Clear filters
            </button>
          )}
        </div>

        {currentlyLoading ? (
          <>
            <div className="hidden sm:block global-border-radius border border-brand-stroke overflow-hidden bg-brand-card">
              <div
                className="grid border-b-2 border-brand-stroke bg-brand-surface/60"
                style={{ gridTemplateColumns: 'minmax(0, 425px) 115px 100px 120px 160px 140px' }}
              >
                {['Plan', 'Data', 'Validity', 'Type', 'Price', ''].map((h, i) => (
                  <div key={i} className="px-5 py-3.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-muted">{h}</span>
                  </div>
                ))}
              </div>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="grid items-center border-b border-brand-stroke/40 last:border-0 animate-pulse"
                  style={{ gridTemplateColumns: 'minmax(0, 425px) 115px 100px 120px 160px 140px' }}
                >
                  <div className="px-5 py-4"><div className="h-4 w-44 bg-brand-stroke/40 rounded" /></div>
                  {[1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="px-4 py-4 flex justify-center">
                      <div className="h-4 w-12 bg-brand-stroke/30 rounded" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="sm:hidden grid grid-cols-1 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <PlanCardSkeleton key={i} />)}
            </div>
          </>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 global-border-radius border border-brand-stroke bg-brand-card">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-surface">
              <Zap className="h-5 w-5 text-brand-muted" />
            </div>
            <p className="text-sm text-brand-muted">No plans available for this destination.</p>
          </div>
        ) : (
          <>
            <div className="hidden sm:block global-border-radius border border-brand-stroke overflow-hidden bg-brand-card">
              <div
                className="grid border-b-2 border-brand-stroke bg-brand-surface/60"
                style={{ gridTemplateColumns: 'minmax(0, 425px) 115px 100px 120px 160px 140px' }}
              >
                {['Plan', 'Data', 'Validity', 'Type', 'Price', ''].map((h, i) => (
                  <div key={i} className={`px-5 py-3.5 ${i > 0 ? 'text-center' : ''}`}>
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-muted">{h}</span>
                  </div>
                ))}
              </div>
              {items.map((item, idx) => (
                <PlanTableRow key={item.slug} item={item} isLast={idx === items.length - 1} isSelected={item.slug === selectedSlug} />
              ))}
            </div>
            <div className="sm:hidden grid grid-cols-1 gap-4">
              {items.map((item) => (
                <PlanCard
                  key={item.slug}
                  plan={item as unknown as PublicPlan}
                  className={item.slug === selectedSlug ? 'ring-2 ring-brand-green border-brand-green' : undefined}
                />
              ))}
            </div>
          </>
        )}

        {!currentlyLoading && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-brand-stroke/40">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => { interact(); setPage(p => p - 1) }}
              className="flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2.5 global-border-radius border border-brand-stroke bg-brand-card text-sm font-semibold text-brand-muted hover:text-brand-text hover:border-brand-green/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let p: number
                if (totalPages <= 7) p = i + 1
                else if (page <= 4) p = i + 1
                else if (page >= totalPages - 3) p = totalPages - 6 + i
                else p = page - 3 + i
                return (
                  <button
                    key={p}
                    onClick={() => { interact(); setPage(p) }}
                    className={[
                      'min-w-[2.25rem] h-9 global-border-radius text-xs font-bold transition-all duration-150',
                      p === page
                        ? 'bg-brand-grey text-brand-bg shadow-sm'
                        : 'text-brand-muted hover:text-brand-text hover:bg-brand-surface',
                    ].join(' ')}
                  >
                    {p}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => { interact(); setPage(p => p + 1) }}
              className="flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2.5 global-border-radius border border-brand-stroke bg-brand-card text-sm font-semibold text-brand-muted hover:text-brand-text hover:border-brand-green/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <DestinationCTA />
      </div>
    </div>
  )
}

function RegionCountriesInfo({ countries, regionName }: { countries: { iso2: string; name: string }[]; regionName: string }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = search.trim()
    ? countries.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : countries

  return (
    <>
      <div className="flex items-center gap-1 mt-0.5">
        <p className="text-xs text-brand-muted truncate">
          {countries.length} countries
        </p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setSearch(''); setOpen(true) }}
          className="flex-shrink-0 text-brand-muted hover:text-brand-text transition-colors"
          aria-label={`View countries in ${regionName}`}
        >
          <Info className="h-3 w-3" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-w-xs sm:max-w-sm bg-brand-card border-brand-stroke rounded-3xl p-0 gap-0 overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-6 pt-6 pb-5 border-b border-brand-stroke/15 bg-brand-surface/40">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted">
                {countries.length} Countries Covered
              </p>
              <DialogTitle className="text-base font-semibold text-brand-text leading-tight">
                {regionName}
              </DialogTitle>
            </div>
            <DialogClose className="mt-0.5 rounded-full p-1.5 text-brand-muted hover:text-brand-text hover:bg-brand-stroke/20 transition-colors">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          <div className="px-6 py-3 border-b border-brand-stroke/10">
            <div className="flex items-center gap-2 bg-brand-surface rounded-full px-3 py-2">
              <Search className="h-3.5 w-3.5 text-brand-muted shrink-0" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="flex-1 bg-transparent text-xs text-brand-text placeholder:text-brand-muted outline-none"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')}>
                  <X className="h-3 w-3 text-brand-muted hover:text-brand-text transition-colors" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col max-h-[50vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="py-8 text-xs text-center text-brand-muted">No countries found.</p>
            ) : (
              filtered.map((c) => (
                <div key={c.iso2} className="flex items-center gap-3 px-6 py-2.5 border-b border-brand-stroke/10 last:border-0">
                  <div className="flex items-center justify-center overflow-hidden rounded h-4 w-6 shrink-0">
                    <Flag code={c.iso2.toLowerCase()} width={22} height={22} circle={true} />
                  </div>
                  <span className="text-xs text-brand-text/80">{c.name}</span>
                </div>
              ))
            )}
          </div>

          <div className="px-6 py-3.5 bg-brand-surface/30 border-t border-brand-stroke/10">
            <p className="text-xs text-brand-muted">
              {search ? `${filtered.length} of ${countries.length} countries` : `${countries.length} countries total`}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function PlanTableRow({ item, isLast, isSelected }: { item: DataPlan; isLast: boolean; isSelected?: boolean }) {
  const gb = parseFloat(item.data_quota_gb)
  const isUnlimited = item.data_plan_type === 'unlimitedStandard'
  const original = item.retail_price_converted_guest
  const discounted = item.retail_price_converted_after_offer
  const hasPromo = typeof original === 'number' && discounted < original

  return (
    <div
      className={[
        "grid items-center hover:bg-brand-surface/20 transition-colors duration-150 group",
        !isLast ? "border-b border-brand-stroke/40" : "",
        isSelected ? "bg-brand-green/5 border-l-2 border-l-brand-green" : "",
      ].join(" ")}
      style={{ gridTemplateColumns: 'minmax(0, 425px) 115px 100px 120px 160px 140px' }}
    >
      <div className="px-5 py-4 min-w-0">
        <p className="text-base 3xl:text-xl font-semibold text-brand-text truncate">{item.name}</p>
        {item.plan_type === 'region' && item.countries.length > 0 && (
          <RegionCountriesInfo
            countries={item.countries as unknown as { iso2: string; name: string }[]}
            regionName={item.region || ''}
          />
        )}
        {item.plan_type === 'country' && item.network_operators && item.network_operators.length > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Signal className="h-3 w-3 text-brand-muted/60 shrink-0" />
            {item.network_operators.slice(0, 3).map((op) => (
              <span key={op.network_name} className="inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-brand-surface border border-brand-stroke/40 text-brand-muted">
                {op.network_name}
                {op.supports_5g && <span className="text-brand-green font-bold">· 5G</span>}
                {!op.supports_5g && op.supports_4g_lte && <span className="text-brand-teal font-bold">· 4G</span>}
              </span>
            ))}
            {item.network_operators.length > 3 && (
              <span className="text-xs text-brand-muted/60">+{item.network_operators.length - 3}</span>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          {item.recommend && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius bg-brand-green/15 border border-brand-green/25 text-brand-green">
              <Sparkles className="h-2.5 w-2.5" />
              Recommended
            </span>
          )}
        </div>
      </div>

      <div className="px-4 py-4 text-center">
        <span className="text-sm font-semibold text-brand-text">
          {isUnlimited ? 'Unlimited' : `${gb % 1 === 0 ? gb.toFixed(0) : gb} GB`}
        </span>
      </div>

      <div className="px-4 py-4 text-center">
        <span className="text-sm text-brand-text">{item.validity_days}d</span>
      </div>

      <div className="px-4 py-4 text-center">
        {isUnlimited ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius bg-brand-purple/10 border border-brand-purple text-brand-purple">
            <Zap className="h-2.5 w-2.5" />
            Unlimited
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 global-border-radius bg-brand-teal/15 border border-brand-green/25 text-brand-green">
            Fixed
          </span>
        )}
      </div>

      <div className="px-4 py-4 text-center">
        {hasPromo ? (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-brand-muted line-through tabular-nums">${original.toFixed(2)}</span>
              {/* <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold bg-brand-green/15 border border-brand-green/25 text-brand-green uppercase tracking-wider">
                -{Math.round(((original - discounted) / original) * 100)}%
              </span> */}
            </div>
            <span className="text-base font-bold text-brand-text tabular-nums">${discounted.toFixed(2)} AUD</span>
          </div>
        ) : (
          <span className="text-base font-bold text-brand-text tabular-nums">${discounted.toFixed(2)} AUD</span>
        )}
      </div>

      <div className="px-4 py-4 flex justify-end">
        <Link
          href={`/public-esim/checkout?slug=${item.slug}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 global-border-radius text-sm font-bold bg-brand-grey text-brand-card hover:bg-brand-grey/80 transition-colors whitespace-nowrap"
        >
          <Wifi className="h-3.5 w-3.5" />
          Get Plan
        </Link>
      </div>
    </div>
  )
}

function NotFound() {
  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center gap-6 px-6">
      <div className="flex flex-col items-center gap-4 text-center max-w-sm">
        <div className="flex h-16 w-16 items-center justify-center global-border-radius bg-brand-card border border-brand-stroke">
          <Globe className="h-7 w-7 text-brand-muted" />
        </div>
        <h1 className="text-2xl font-bold text-brand-text">Destination Not Found</h1>
        <p className="text-sm text-brand-muted leading-relaxed">
          We couldn&apos;t find eSIM plans for this destination. Browse our full catalogue to find the right plan for your trip.
        </p>
        <Link
          href="/travel-esim"
          className="inline-flex items-center gap-2 rounded-full bg-brand-grey px-6 py-3 text-sm font-bold text-brand-bg hover:opacity-90 transition-all"
        >
          Browse all destinations
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
